import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/problems`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("nex_token")}` } });
const MONEY = new Set(["paycheck_flagged", "paycheck_duplicate", "paycheck_superseded", "invoice_overpaid"]);
const letters = (s) => (s.trim().match(/\p{L}/gu) || []).length;

/** Reason box shared by single and bulk clearing: ≥5 letters, ≤500 chars, warning (+ money note), Confirm disabled until valid. */
function ReasonBox({ money, testid, onConfirm, onCancel, label = "Confirm" }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = letters(reason) >= 5 && reason.length <= 500;
  const confirm = async () => {
    if (!ok || busy) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); } finally { setBusy(false); }
  };
  const setOpen = (v) => { if (!v) onCancel(); };
  return (
    <div className="w-full mt-2 p-3 border border-white/15 bg-black/30 rounded-sm space-y-2" data-testid={`${testid}-obsolete-box`}>
      <p className="text-[11px] text-amber-200">This hides the problem. It does not change any money, document, or score.</p>
      {money && <p className="text-[11px] text-sky-200" data-testid={`${testid}-obsolete-money-note`}>The money stays as it is. If it needs correcting, post a ledger correction first.</p>}
      <textarea rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this obsolete? (e.g. old portal record)" data-testid={`${testid}-obsolete-reason`}
        className="w-full px-2 py-1.5 bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver text-xs rounded-sm" />
      {reason && !ok && <p className="text-[11px] text-red-300" data-testid={`${testid}-obsolete-error`}>Reason needs at least 5 letters.</p>}
      <div className="flex gap-3">
        <button type="button" disabled={!ok || busy} onClick={confirm} data-testid={`${testid}-obsolete-confirm`}
          className="px-2.5 py-1 border border-nex-gold/60 text-nex-gold font-mono text-[9px] tracking-[0.16em] uppercase rounded-sm hover:bg-nex-gold/10 disabled:opacity-40">{label}</button>
        <button type="button" onClick={() => { setOpen(false); setReason(""); }} data-testid={`${testid}-obsolete-cancel`} className="font-mono text-[9px] uppercase text-nex-muted">Cancel</button>
      </div>
    </div>
  );
}

/** Super admin: hide one stale problem for this exact record, with a required reason. Never changes money, documents or scores. */
export function MarkObsolete({ problem, onCleared, onRefresh, testid }) {
  const [open, setOpen] = useState(false);
  const confirm = async (reason) => {
    try {
      await axios.post(`${API}/obsolete`, { problem_id: problem.id, reason }, auth());
      toast.success("Marked obsolete — moved to Cleared as obsolete");
      onCleared(problem.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark it obsolete");
      onRefresh();
    }
  };
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-obsolete`}
      className="inline-flex items-center gap-1 px-2.5 py-1 border border-white/15 text-nex-muted font-mono text-[9px] tracking-[0.16em] uppercase rounded-sm hover:text-nex-silver"><EyeOff className="h-3 w-3" /> Mark obsolete</button>
  );
  return <ReasonBox money={MONEY.has(problem.code)} testid={testid} onConfirm={confirm} onCancel={() => setOpen(false)} />;
}

/** Bulk bar: tick several problems, give one reason, clear them all. Problems already cleared or fixed are reported, not fatal. */
export function BulkObsolete({ selected, problems, onDone, onRefresh }) {
  const [open, setOpen] = useState(false);
  if (!selected.length) return null;
  const money = problems.some((p) => selected.includes(p.id) && MONEY.has(p.code));
  const confirm = async (reason) => {
    try {
      const { data } = await axios.post(`${API}/obsolete/bulk`, { problem_ids: selected, reason }, auth());
      toast.success(`${data.cleared.length} marked obsolete${data.skipped.length ? ` · ${data.skipped.length} skipped (${data.skipped[0].detail})` : ""}`);
      setOpen(false);
      onDone(data.cleared);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't mark them obsolete"); onRefresh(); }
  };
  return (
    <div className="p-3 border border-nex-gold/30 bg-nex-gold/[0.04] rounded-sm" data-testid="problems-bulk-bar">
      <div className="flex items-center gap-3">
        <span className="text-sm text-nex-silver" data-testid="problems-bulk-count">{selected.length} selected</span>
        {!open && <button type="button" onClick={() => setOpen(true)} data-testid="problems-bulk-obsolete"
          className="inline-flex items-center gap-1 px-2.5 py-1 border border-nex-gold/60 text-nex-gold font-mono text-[9px] tracking-[0.16em] uppercase rounded-sm hover:bg-nex-gold/10"><EyeOff className="h-3 w-3" /> Mark {selected.length} obsolete</button>}
      </div>
      {open && <ReasonBox money={money} testid="problems-bulk" label={`Clear ${selected.length}`} onConfirm={confirm} onCancel={() => setOpen(false)} />}
    </div>
  );
}

/** One row in 'Cleared as obsolete': what it was, why, who cleared it and when, plus Restore. */
export function ClearedRow({ c, onRestored, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/obsolete/restore`, { key: c.key }, auth());
      toast.success(data.still_open ? "Restored to the open list" : "Restored — the record is already fixed");
      onRestored();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't restore"); onRefresh(); } finally { setBusy(false); }
  };
  const s = c.snapshot || {};
  return (
    <li data-testid={`cleared-${c.key}`} className="px-4 py-3 space-y-1">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm text-nex-silver/80 font-medium">{s.title}</span>
        {s.resident_name && <span className="text-sm text-nex-silver">· {s.resident_name}</span>}
        <button type="button" disabled={busy} onClick={restore} data-testid={`cleared-restore-${c.key}`}
          className="ml-auto px-2.5 py-1 border border-emerald-400/60 text-emerald-300 font-mono text-[9px] tracking-[0.16em] uppercase rounded-sm hover:bg-emerald-500/10 disabled:opacity-40">Restore</button>
      </div>
      {s.detail && <div className="text-xs text-nex-muted">{s.detail}</div>}
      <div className="text-xs text-nex-silver/80" data-testid={`cleared-reason-${c.key}`}>Reason: {c.reason}</div>
      <div className="font-mono text-[10px] text-nex-muted" data-testid={`cleared-by-${c.key}`}>Cleared by {c.cleared_by?.name} · {new Date(c.cleared_at).toLocaleString()}</div>
    </li>
  );
}
